
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import EditDistributorModal from './components/EditDistributorModal.vue';
import TerminalReceiverModal from './components/TerminalReceiverModal.vue';
import InvoiceTitleModal from './components/InvoiceTitleModal.vue';
import lazyInject from '@/di';
import SupplyService from '@/services/supply.service';
import RegionService from '@/services/region.service';
import { IResRegion } from '@/common/interface/region';
import { CDistributorList } from '@/models/supply.model';

@Component({
  components: {
    EditDistributorModal,
    TerminalReceiverModal,
    InvoiceTitleModal,
  },
})
export default class DistributorList extends Vue {
  loading = false;

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  @lazyInject(RegionService)
  protected regionService!: RegionService;

  regionOptions: IResRegion[] = [];

  filterForm: any = {
    code: '',
    companyName: '',
    province: undefined,
    contactName: '',
    contactPhone: '',
    dLevel: undefined,
    status: undefined,
  };

  dataSource: any[] = [];

  columns = [
    {
      title: '编码',
      dataIndex: 'sdCode',
      align: 'center',
      width: 100,
    },
    {
      title: '公司全称',
      dataIndex: 'companyName',
      align: 'center',
    },
    {
      title: '省份',
      dataIndex: 'province',
      align: 'center',
      width: 100,
    },
    {
      title: '负责区域',
      dataIndex: 'salesRegion',
      align: 'center',
    },
    {
      title: '级别',
      dataIndex: 'dLevel',
      align: 'center',
      width: 100,
      customRender: (text: any) => {
        return text === 1 ? '省级' : '地市级';
      },
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      align: 'center',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      align: 'center',
      width: 130,
    },
    {
      title: '状态',
      dataIndex: 'status',
      align: 'center',
      width: 90,
      scopedSlots: { customRender: 'status' },
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'center',
      width: 280,
      fixed: 'right',
      scopedSlots: { customRender: 'action' },
    },
  ];

  pagination: any = {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: this.$store.state.pageSize.pageSize,
    showTotal: () => `共${this.pagination.total}条`,
    onChange: this.paginationChange,
    onShowSizeChange: this.onShowSizeChange,
  };

  @Ref('editDistributorModal')
  editDistributorModal!: EditDistributorModal;

  @Ref('terminalReceiverModal')
  terminalReceiverModal!: TerminalReceiverModal;

  @Ref('invoiceTitleModal')
  invoiceTitleModal!: InvoiceTitleModal;

  mounted() {
    this.getRegions();
    this.onRouteChange();
  }

  async getRegions() {
    if (this.regionOptions.length) {
      return;
    }
    const res = await this.regionService.allRegions();
    if (res && res.length) {
      this.regionOptions = [...res];
    }
  }

  @Watch('$route')
  onRouteChange() {
    const {
      page,
      pageSize,
      code,
      companyName,
      dLevel,
      province,
      contactName,
      contactPhone,
      status,
    } = this.$route.query;
    const params: any = {
      page: page || 1,
      pageSize: pageSize || 10,
    };
    if (code) {
      params.sd_code = code;
      this.filterForm.code = code;
    }
    if (companyName) {
      params.company_name = companyName;
      this.filterForm.companyName = companyName;
    }
    if (province) {
      params.province = province;
      this.filterForm.province = province;
    }
    if (contactName) {
      params.contact_name = contactName;
      this.filterForm.contactName = contactName;
    }
    if (contactPhone) {
      params.contact_phone = contactPhone;
      this.filterForm.contactPhone = contactPhone;
    }
    if (status !== undefined && status !== '') {
      params.status = Number(status);
      this.filterForm.status = Number(status);
    }
    if (dLevel !== undefined && dLevel !== '') {
      params.d_level = Number(dLevel);
      this.filterForm.dLevel = Number(dLevel);
    }
    this.getList(params);
  }

  async getList(params: any) {
    // TODO: 调用经销商列表接口，查询参数：params
    const res = await this.supplyService.getDistributorList(params, this.$el);
    if (res) {
      this.dataSource = res.list.map(
        (item: any) => CDistributorList.from(item),
        // eslint-disable-next-line function-paren-newline
      );
      // console.log(this.dataSource);
      this.pagination.total = res.total;
      this.pagination.current = res.page;
      this.pagination.pageSize = res.pageSize;
    } else {
      this.dataSource = [];
      this.pagination.total = 0;
    }
  }

  refresh() {
    this.onRouteChange();
  }

  search() {
    this.pagination.current = 1;
    this.pushRoute();
  }

  resetQuery() {
    this.filterForm = {
      code: '',
      companyName: '',
      province: undefined,
      contactName: '',
      contactPhone: '',
      dLevel: undefined,
      status: undefined,
    };
    this.pagination.current = 1;
    this.pushRoute();
  }

  paginationChange(page: number, pageSize: number) {
    this.pagination.current = page;
    this.pagination.pageSize = pageSize;
    this.pushRoute();
  }

  onShowSizeChange(current: number, pageSize: number) {
    this.pagination.current = 1;
    this.pagination.pageSize = pageSize;
    this.pushRoute();
  }

  pushRoute() {
    const { code, companyName, dLevel, province, contactName, contactPhone, status } =
      this.filterForm;
    const { current, pageSize } = this.pagination;
    const query: any = {
      page: current,
      pageSize,
      timestamp: Math.round(Date.now() / 1000),
    };
    if (code) {
      query.code = code;
    }
    if (companyName) {
      query.companyName = companyName;
    }
    if (province) {
      query.province = province;
    }
    if (contactName) {
      query.contactName = contactName;
    }
    if (contactPhone) {
      query.contactPhone = contactPhone;
    }
    if (status !== undefined && status !== '') {
      query.status = status;
    }
    if (dLevel !== undefined && dLevel !== '') {
      query.dLevel = dLevel;
    }
    this.$router.push({ query });
  }

  openEditModal(record: any) {
    this.editDistributorModal.open(record);
  }

  addDistributor() {
    this.editDistributorModal.open();
  }

  openReceiverModal(record: any) {
    this.terminalReceiverModal.open(record);
  }

  openInvoiceTitleModal(record: any) {
    this.invoiceTitleModal.open(record);
  }
}
